<template>
  <v-card>
    <v-card-title :class="`${colorClassForContractType(contract.type)} white--text headline`">
      {{ $t('products') }}
      <v-spacer/>
      <BaseDialogActions
      hideRefresh
      hideFullscreen
      @close="$emit('close')"/>
    </v-card-title>
    <v-card-text>
      <FormWrapper
      @submit="saveBtnClicked"
      :buttonColor="colorClassForContractType(contract.type)">
        <v-row class="mt-2 mb-n3">
          <v-col cols="12">
            <draggable
            class="mb-4"
            v-model="draggableProducts"
            animation=400
            swapThreshold=".75"
            tag="v-row"
            :component-data="{ props: { dense: true } }">
              <v-col cols="12" v-for="product in draggableProducts" :key="product.productId">
                <v-sheet :style="{ fontSize: '12px', padding: '8px', cursor: 'move' }" outlined>
                  <v-row dense align="center">
                    <v-col cols="auto" class="pr-0 py-0 px-1 subtitle-1">
                      {{ product.name }}
                    </v-col>
                    <v-spacer/>
                    <Icon
                    v-if="getDisplayFrequencyForTooltip(product.productId) !== null"
                    icon="mdi-crosshairs-gps"
                    :small="false"
                    iconColor="black"
                    :tooltipText="$t('destinationScaleFrequency', { frequency: getDisplayFrequencyForTooltip(product.productId), tractDestination: contract.destination })"
                    />
                    <v-col cols="2" v-if="showScaleFrequencyInput(product)">
                      <v-text-field
                        v-model="product.scaleFrequency"
                        :rules="[rules.positiveIntegerBetweenOrBlank(0, 100)]"
                        dense
                        hide-details="auto"
                        color="primary"
                        class="ma-0 mt-n1"
                        type="number"
                        hide-spin-buttons
                      >
                        <template #prepend-inner>
                          <span class="grey--text body-2 mt-1">1:</span>
                        </template>
                      </v-text-field>
                    </v-col>
                    <v-col cols="1" align="right">
                      <Icon
                      icon="mdi-delete-forever"
                      :small="false"
                      @icon-clicked="removeProduct(product)"
                      :tooltipText="$t('removeProduct')"/>
                    </v-col>
                  </v-row>
                </v-sheet>
              </v-col>
            </draggable>
          </v-col>
        </v-row>
        <v-row class="mt-n6 mb-3">
          <v-col col="12">
            <Icon
            icon="mdi-information-outline"
            iconColor="info"
            :small="false"/>
            <em>
              {{ $t('dragAndDropToReorderProducts') }}
            </em>
          </v-col>
        </v-row>
        <v-row v-if="canDefineScaleFrequency" dense class="mb-2">
          <v-col cols="12">
            <v-checkbox
              v-model="defineScaleFrequencies"
              :label="$t('defineScaleSampleFrequency')"
              dense
              hide-details
              class="ma-0 pa-0"
            />
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" class="px-2">
            <ProductAutocomplete
            ref="productAutocomplete"
            :contract="contract"
            :limitToContractProducts="false"
            :excludedProducts="draggableProducts.map(p => p.name)"
            :filteredProductIds="productIdsToFilterOn"
            hideRefresh
            :clearable="false"
            @product-chosen="addProduct"/>
          </v-col>
        </v-row>
      </FormWrapper>
    </v-card-text>
  </v-card>
</template>

<script>
import { colorClassForContractType } from '@/utils/componentHelpers.js'
import { mapActions } from 'vuex'
import draggable from 'vuedraggable'
import fieldRules from '@/utils/rules.js'
import { PayBy, ContractType } from '@/utils/Enumerations'
export default {
  name: 'ContractProductForm',

  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    FormWrapper: () => import('@/components/core/FormWrapper.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    ProductAutocomplete: () => import('@/components/autocomplete/ProductAutocomplete.vue'),
    draggable
  },

  props: {
    products: { type: Array, required: true },
    contract: { type: Object, required: true },
    activities: { type: Array, required: true }
  },

  data: () => ({
    draggableProducts: [],
    tractDestinationProducts: undefined,
    rules: fieldRules.rules,
    destinationAccount: null,
    defineScaleFrequencies: false,
    accountProductIds: []
  }),

  async created () {
    if (this.contract.tractId > 0) {
      const destinations = await this.fetchTractDestinations(this.contract.tractId)
      const contractDestination = destinations.find(c => c.destinationAccountId === this.contract.destinationAccountId)
      this.tractDestinationProducts = contractDestination?.products
    }

    if (this.canDefineScaleFrequency || this.activities.some(a => a.baseCost?.payBy === PayBy.Volume.value)) {
      this.destinationAccount = await this.fetchAccountById(this.contract.destinationAccountId)
      this.accountProductIds = this.destinationAccount.scalingProducts?.map(p => p.productId) ?? []
    }

    const hasAnyExistingFrequency = this.products.some(p => p.scaleFrequency != null && parseFloat(p.scaleFrequency) > 0)
    this.defineScaleFrequencies = hasAnyExistingFrequency

    this.draggableProducts = this.products.toSorted((a, b) => a.sortOrder - b.sortOrder).map(p => {
      let serverFreq = parseFloat(p.scaleFrequency)
      if (serverFreq == null || isNaN(serverFreq)) {
        serverFreq = this.tractDestinationProducts?.find(tdP => tdP.productId === p.productId)?.scaleFrequency || 0
      }

      const displayFreq = serverFreq && serverFreq > 0 ? Math.round(100 / serverFreq) : 1

      return {
        ...p,
        scaleFrequency: displayFreq
      }
    })
  },

  computed: {
    mappedProducts () {
      return this.draggableProducts.map((p, idx) => ({ ...p, sortOrder: idx }))
    },

    productIdsToFilterOn () {
      if (this.tractDestinationProducts?.length > 0) {
        return this.tractDestinationProducts.map(p => p.productId)
      }

      if (this.accountProductIds?.length > 0) {
        return this.accountProductIds
      }

      return undefined
    },

    canDefineScaleFrequency () {
      return (this.contract.type === ContractType.Production.value || this.contract.type === ContractType.WoodsSale.value) &&
        this.activities.some(a => a.baseCost?.payBy === PayBy.Volume.value)
    }
  },

  methods: {
    ...mapActions('contract', ['updateContractProducts']),
    ...mapActions('tract-destination', ['fetchTractDestinations']),
    ...mapActions('account', ['fetchAccountById']),
    colorClassForContractType,

    shouldFallbackDefineFrequency (productId) {
      return this.destinationAccount?.fallbackExpands === true && this.destinationAccount?.fallbackProductId === productId
    },

    showScaleFrequencyInput (product) {
      return this.canDefineScaleFrequency && this.defineScaleFrequencies && (this.accountProductIds.includes(product.productId) || this.shouldFallbackDefineFrequency(product.productId))
    },

    async saveBtnClicked () {
      const invalidProduct = this.mappedProducts.find(p => {
        if (!this.showScaleFrequencyInput(p)) return false
        const val = Number(p.scaleFrequency)
        return !Number.isInteger(val) || val <= 0 || val > 100
      })

      if (invalidProduct) {
        this.setSnackError(this.$t('invalidScaleFrequencyError'))
        return
      }
      const contractProducts = this.mappedProducts.map(({ name, productId, sortOrder, scaleFrequency }) => {
        const shouldSaveFrequency = this.showScaleFrequencyInput({ productId })
        return {
          name,
          productId,
          sortOrder,
          scaleFrequency: shouldSaveFrequency ? parseFloat((100 / scaleFrequency).toFixed(3)) : null
        }
      })

      try {
        await this.updateContractProducts({ contractId: this.contract.contractId, products: contractProducts })
        this.$emit('close', contractProducts)
      } catch (_) {
        this.draggableProducts = this.products.toSorted((a, b) => a.sortOrder - b.sortOrder)
      }
    },

    addProduct (chosenProduct) {
      if (chosenProduct == null) return
      const { productId, name } = chosenProduct
      this.draggableProducts.push({ productId, name })
      this.$refs.productAutocomplete.chosenProduct = null
    },

    getDisplayFrequencyForTooltip (productId) {
      const product = this.tractDestinationProducts?.find(p => p.productId === productId)
      if (product == null || product.scaleFrequency == null || product.scaleFrequency <= 0) {
        return null
      }
      return `1:${Math.round(100 / product.scaleFrequency)}`
    },

    removeProduct (product) {
      if (this.activities.some(a => a.activityDetails.some(d => d.product.productId === product.productId))) {
        this.setSnackError(this.$t('cannotRemoveProductWithActivityDetail', { product: product.name }))
        return
      }
      this.draggableProducts = this.draggableProducts.filter(p => p.productId !== product.productId)
    }
  }
}
</script>
<style scoped>
.sortable-ghost {
  background-color: rgba(0, 0, 0, 0.6);
  border-radius: 4px;
}
</style>
